/**
 * Tokenize a card via Authorize.Net Accept.js.
 *
 * Loads no script tag — the Accept.js library is injected in `src/index.tsx`
 * for the right environment (sandbox vs production) and exposes
 * `window.Accept.dispatchData`.
 *
 * Resolves with the opaque-token pair the BE forwards to CARA as
 * `payment_descriptor` / `payment_token`. Rejects with a user-friendly
 * `Error` when AuthNet declines or when the script never loaded.
 *
 * This used to live inline in <PaymentForm>; lifted out so the shared
 * <CheckoutShell> / <AddCardPanel> can use it too. Previously those
 * called a `buildAcceptJsTokenPlaceholder` stub that returned literal
 * `"PHASE1_PLACEHOLDER"` strings — the migration to the shared shell
 * left the real Accept.js call unwired.
 */

import errorCodeToMessage from "@/modules/eventRegistration/features/EventRegistration/utils/errorMappings";
import type { AcceptJsToken } from "../types";
import type { InlinePaymentFields } from "../components/InlinePaymentForm/InlinePaymentForm";

type AcceptResponse = {
  messages: {
    resultCode: string;
    message: Array<{ code: string; text: string }>;
  };
  opaqueData: {
    dataDescriptor: string;
    dataValue: string;
  };
};

declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        secureData: {
          authData: { clientKey: string; apiLoginID: string };
          cardData: {
            cardNumber: string;
            month: string;
            year: string;
            cardCode: string;
            fullName: string;
          };
        },
        callback: (response: AcceptResponse) => void,
      ) => void;
    };
  }
}

export async function tokenizeCard(
  fields: InlinePaymentFields,
): Promise<AcceptJsToken> {
  const apiLoginID = import.meta.env.VITE_AUTHNET_API_LOGIN_ID ?? "";
  const clientKey = import.meta.env.VITE_AUTHNET_CLIENT_KEY ?? "";

  if (!apiLoginID || !clientKey) {
    throw new Error(
      "Payment is misconfigured (missing Accept.js keys). Please contact support.",
    );
  }
  if (!window.Accept || typeof window.Accept.dispatchData !== "function") {
    throw new Error(
      "Payment functionality is currently unavailable. Please refresh and try again, or contact support.",
    );
  }

  return new Promise<AcceptJsToken>((resolve, reject) => {
    window.Accept!.dispatchData(
      {
        authData: { clientKey, apiLoginID },
        cardData: {
          cardNumber: fields.cardNumber.replace(/\s+/g, ""),
          month: fields.expMonth,
          year: fields.expYear,
          cardCode: fields.cvc,
          fullName: fields.cardholderName,
        },
      },
      (response) => {
        if (response.messages.resultCode === "Error") {
          const messages = Array.isArray(response.messages.message)
            ? response.messages.message.map(
                (m) => errorCodeToMessage[m.code] ?? m.text,
              )
            : ["Could not tokenize card."];
          reject(new Error(messages.join("\n")));
          return;
        }

        const rawPan = fields.cardNumber.replace(/\s+/g, "");
        resolve({
          dataDescriptor: response.opaqueData.dataDescriptor,
          dataValue: response.opaqueData.dataValue,
          cardExpiration: `${fields.expMonth}/${fields.expYear}`,
          last4: rawPan.slice(-4),
          name: fields.cardholderName.trim(),
        });
      },
    );
  });
}
